/**
 * CompareDropdown Component
 *
 * Dropdown for adding comparison institutions to the chart.
 * Features search, current rating badges, and selection indicators.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, Check, X } from 'lucide-react';
import { useRatedInstitutionsWithRatings } from '../../../../hooks/useInstitutions';
import { shortenInstitutionName } from '../../../../utils/institutionNames';
import styles from './TimelineChart.module.css';

interface CompareDropdownProps {
  /** Currently selected comparison institutions */
  selectedInstitutions: Array<{ id: string; name: string }>;
  /** Callback when an institution is added */
  onAdd: (institution: { id: string; name: string; rating?: string }) => void;
  /** Callback when an institution is removed */
  onRemove: (id: string) => void;
  /** ID of the primary institution (to exclude from list) */
  excludeId: string;
  /** Maximum number of comparison institutions allowed */
  maxCompare?: number;
}

export default function CompareDropdown({
  selectedInstitutions,
  onAdd,
  onRemove,
  excludeId,
  maxCompare = 3,
}: CompareDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Get institutions list with their ratings
  const { institutions, isLoading: institutionsLoading } = useRatedInstitutionsWithRatings();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when opening
      setTimeout(() => searchRef.current?.focus(), 100);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Filter institutions based on search and exclusions
  const filteredInstitutions = institutions.filter((inst) => {
    // Exclude primary institution
    if (inst.institution_id === excludeId) return false;
    // Search filter - search both full name and short name
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const shortName = shortenInstitutionName(inst.institution_name);
      return (
        inst.institution_name.toLowerCase().includes(query) ||
        shortName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const isSelected = (id: string) => selectedInstitutions.some((s) => s.id === id);
  const isMaxReached = selectedInstitutions.length >= maxCompare;

  const handleToggle = (inst: { institution_id: string; institution_name: string; rating_category?: string }) => {
    if (isSelected(inst.institution_id)) {
      onRemove(inst.institution_id);
    } else if (!isMaxReached) {
      onAdd({
        id: inst.institution_id,
        name: inst.institution_name,
        rating: inst.rating_category,
      });
    }
  };

  const buttonLabel = selectedInstitutions.length > 0
    ? t('compareChart.compareCount', { count: selectedInstitutions.length })
    : t('compareChart.compare');

  return (
    <div className={styles.compareDropdown} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.compareBtn} ${isOpen ? styles.open : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{buttonLabel}</span>
        <ChevronDown size={14} className={styles.chevron} />
      </button>

      {isOpen && (
        <div className={styles.compareMenu} role="listbox">
          <div className={styles.searchWrapper}>
            <Search size={14} className={styles.searchIcon} />
            <input
              ref={searchRef}
              type="text"
              placeholder={t('compareChart.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button
                type="button"
                className={styles.clearSearch}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className={styles.institutionList}>
            {institutionsLoading ? (
              <div className={styles.loadingState}>{t('compareChart.loadingInstitutions')}</div>
            ) : filteredInstitutions.length === 0 ? (
              <div className={styles.emptyState}>
                {searchQuery ? t('compareChart.noInstitutionsFound') : t('compareChart.noInstitutionsAvailable')}
              </div>
            ) : (
              filteredInstitutions.map((inst) => {
                const selected = isSelected(inst.institution_id);
                const disabled = !selected && isMaxReached;
                const displayName = shortenInstitutionName(inst.institution_name);

                return (
                  <button
                    key={inst.institution_id}
                    type="button"
                    className={`${styles.institutionItem} ${selected ? styles.selected : ''} ${disabled ? styles.disabled : ''}`}
                    onClick={() => handleToggle(inst)}
                    disabled={disabled}
                    role="option"
                    aria-selected={selected}
                    title={inst.institution_name}
                  >
                    <div className={styles.institutionInfo}>
                      <span className={styles.institutionName}>{displayName}</span>
                      {inst.rating_category && (
                        <span className={styles.ratingBadge}>{inst.rating_category}</span>
                      )}
                    </div>
                    {selected && (
                      <Check size={14} className={styles.checkIcon} />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {isMaxReached && (
            <div className={styles.maxReachedHint}>
              {t('compareChart.maxReached', { count: maxCompare })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
